import {
  ref,
  defineAsyncComponent,
  markRaw,
  reactive,
} from 'vue';
import { defineStore } from 'pinia';
import { v7 as uuidv7 } from 'uuid';

const componentsStore = defineStore('componentsStore', () => {
  const alertDialog = ref({});
  const changePassword = reactive({});
  const departmentForm = reactive({});
  const administrator = reactive({});
  const rejectPhoneData = reactive({});
  const scheduleSearchResultsDownload = reactive({});
  const scheduledFileDownload = reactive({});
  const imageManagement = reactive({});
  const imageView = reactive({});
  const imageList = reactive({});
  const mfaAuth = reactive({});
  const temporaryPasswordChange = reactive({});
  const alimTalkConfirmDialog = reactive({});
  const alimTalkInquiryDialog = reactive({});
  const applicationExample = reactive({});
  const confirmDialog = reactive({});
  const inspectionLoadingDialog = reactive({});
  const inspectionResultDialog = reactive({});

  const components = ref({});

  const addComponents = ({ component, id }) => {
    components.value[id] = markRaw(component);
  };

  const removeComponent = ({ id }) => {
    delete components.value[id];
  };

  const openAlertDialog = (options = {}) => {
    const alertId = uuidv7();
    const componentModule = defineAsyncComponent(() => import('@/components/dialog/AlertDialog.vue'));

    const {
      autoClose,
      title,
      content,
      titleClass = 'certificationPop',
      bodyClass = '',
      translated,
      button = {
        display: true,
        text: 'common.confirmBtn',
        className: 'lightBlue-button',
      },
      trackingId,
    } = options;

    const promise = new Promise((resolve, reject) => {
      alertDialog.value[alertId] = {
        resolve,
        reject,
        options: {
          title,
          content,
          translated,
          titleClass,
          bodyClass,
          button,
          autoClose: autoClose ?? true,
          trackingId,
        },
        state: true,
      };
    });

    addComponents({ component: componentModule, id: alertId });

    return {
      id: alertId,
      promise,
    };
  };

  const closeAlertDialog = ({ id }) => {
    removeComponent({ id });
  };

  const openChangePasswordDialog = (options = {}) => {
    if (!changePassword.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/PasswordChangeDialog.vue'));
      addComponents({ component: componentModule, id: 'passwordChangeDialog' });
      changePassword.init = true;
    }

    return new Promise((resolve, reject) => {
      changePassword.resolve = resolve;
      changePassword.reject = reject;
      changePassword.options = options;
      changePassword.state = true;
    });
  };

  const closeChangePasswordDialog = () => {
    changePassword.state = false;
  };

  const openDepartmentForm = (options = {}) => {
    if (!departmentForm.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/DepartmentForm.vue'));
      addComponents({ component: componentModule, id: 'departmentForm' });
      departmentForm.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    departmentForm.resolve = resolve;
    departmentForm.reject = reject;
    departmentForm.options = options;
    departmentForm.state = true;

    return promise;
  };

  const closeDepartmentForm = () => {
    departmentForm.state = false;
  };

  const openAdministrator = (options = {}) => {
    if (!administrator.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/AdministratorSettings.vue'));
      addComponents({ component: componentModule, id: 'administratorSettings' });
      administrator.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    administrator.resolve = resolve;
    administrator.reject = reject;
    administrator.options = options;
    administrator.state = true;

    return promise;
  };

  const closeAdministrator = () => {
    administrator.state = false;
  };

  const openRejectPhoneData = (options = {}) => {
    if (!rejectPhoneData.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/RejectPhoneDataDialog.vue'));
      addComponents({ component: componentModule, id: 'rejectPhoneDataSetting' });
      rejectPhoneData.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    rejectPhoneData.resolve = resolve;
    rejectPhoneData.reject = reject;
    rejectPhoneData.options = options;
    rejectPhoneData.state = true;

    return promise;
  };

  const closeRejectPhoneData = () => {
    rejectPhoneData.state = false;
  };

  const openScheduleSearchResultsDownload = (options = {}) => {
    if (!scheduleSearchResultsDownload.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/ScheduleSearchResultsDownloadDialog.vue'));
      addComponents({ component: componentModule, id: 'ScheduleSearchResultsDownloadSetting' });
      scheduleSearchResultsDownload.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    scheduleSearchResultsDownload.resolve = resolve;
    scheduleSearchResultsDownload.reject = reject;
    scheduleSearchResultsDownload.options = options;
    scheduleSearchResultsDownload.state = true;

    return promise;
  };

  const closeScheduleSearchResultsDownload = () => {
    scheduleSearchResultsDownload.state = false;
  };

  const openScheduledFileDownload = (options = {}) => {
    if (!scheduledFileDownload.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/ScheduledFileDownloadDialog.vue'));
      addComponents({ component: componentModule, id: 'ScheduledFileDownloadSetting' });
      scheduledFileDownload.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    scheduledFileDownload.resolve = resolve;
    scheduledFileDownload.reject = reject;
    scheduledFileDownload.options = options;
    scheduledFileDownload.state = true;

    return promise;
  };

  const closeScheduledFileDownload = () => {
    scheduledFileDownload.state = false;
  };

  const openImageManagement = (options = {}) => {
    if (!imageManagement.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/ImageDialog.vue'));
      addComponents({ component: componentModule, id: 'ImageDialogSetting' });
      imageManagement.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    imageManagement.resolve = resolve;
    imageManagement.reject = reject;
    imageManagement.options = options;
    imageManagement.state = true;

    return promise;
  };

  const closeImageManagement = () => {
    imageManagement.state = false;
  };

  const openImageView = (options = {}) => {
    if (!imageView.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/ImageViewDialog.vue'));
      addComponents({ component: componentModule, id: 'ImageViewSetting' });
      imageView.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    imageView.resolve = resolve;
    imageView.reject = reject;
    imageView.options = options;
    imageView.state = true;

    return promise;
  };

  const closeImageView = () => {
    imageView.state = false;
  };

  const openImageList = (options = {}) => {
    if (!imageList.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/ImageListDialog.vue'));
      addComponents({ component: componentModule, id: 'ImageListDialog' });
      imageList.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    imageList.resolve = resolve;
    imageList.reject = reject;
    imageList.options = options;
    imageList.state = true;

    return promise;
  };

  const closeImageList = () => {
    imageList.state = false;
  };

  const openMfaAuth = (options = {}) => {
    if (!mfaAuth.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/MfaAuthDialog.vue'));
      addComponents({ component: componentModule, id: 'MfaAuthDialog' });
      mfaAuth.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    mfaAuth.resolve = resolve;
    mfaAuth.reject = reject;
    mfaAuth.options = options;
    mfaAuth.state = true;

    return promise;
  };

  const closeMfaAuth = () => {
    mfaAuth.state = false;
  };

  const openTemporaryPasswordChange = (options = {}) => {
    if (!temporaryPasswordChange.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/TemporaryPasswordChangeDialog.vue'));
      addComponents({ component: componentModule, id: 'TemporaryPasswordChangeDialog' });
      temporaryPasswordChange.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    temporaryPasswordChange.resolve = resolve;
    temporaryPasswordChange.reject = reject;
    temporaryPasswordChange.options = options;
    temporaryPasswordChange.state = true;

    return promise;
  };

  const closeTemporaryPasswordChange = () => {
    temporaryPasswordChange.state = false;
  };

  const openAlimTalkConfirmDialog = (options = {}) => {
    if (!alimTalkConfirmDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/AlimTalkConfirmDialog.vue'));
      addComponents({ component: componentModule, id: 'alimTalkConfirmDialog' });
      alimTalkConfirmDialog.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    alimTalkConfirmDialog.resolve = resolve;
    alimTalkConfirmDialog.reject = reject;
    alimTalkConfirmDialog.options = options;
    alimTalkConfirmDialog.state = true;

    return promise;
  };

  const closeAlimTalkConfirmDialog = () => {
    alimTalkConfirmDialog.state = false;
  };

  const openAlimTalkInquiryDialog = (options = {}) => {
    if (!alimTalkInquiryDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/AlimTalkInquiryDialog.vue'));
      addComponents({ component: componentModule, id: 'alimTalkInquiryDialog' });
      alimTalkInquiryDialog.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    alimTalkInquiryDialog.resolve = resolve;
    alimTalkInquiryDialog.reject = reject;
    alimTalkInquiryDialog.options = options;
    alimTalkInquiryDialog.state = true;

    return promise;
  };

  const closeAlimTalkInquiryDialog = () => {
    alimTalkInquiryDialog.state = false;
  };

  const openApplicationExampleDialog = (options = {}) => {
    if (!applicationExample.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/applicationExampleDialog.vue'));
      addComponents({ component: componentModule, id: 'applicationExample' });
      applicationExample.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    applicationExample.resolve = resolve;
    applicationExample.reject = reject;
    applicationExample.options = options;
    applicationExample.state = true;

    return promise;
  };

  const closeApplicationExampleDialog = () => {
    applicationExample.state = false;
  };

  const openConfirmDialog = (options = {}) => {
    if (!confirmDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/ConfirmDialog.vue'));
      addComponents({ component: componentModule, id: 'confirmDialog' });
      confirmDialog.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    confirmDialog.resolve = resolve;
    confirmDialog.reject = reject;
    confirmDialog.options = options;
    confirmDialog.state = true;

    return promise;
  };

  const closeConfirmDialog = () => {
    confirmDialog.state = false;
  };

  const openInspectionLoadingDialog = (options = {}) => {
    if (!inspectionLoadingDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/InspectionLoading.vue'));
      addComponents({ component: componentModule, id: 'inspectionLoadingDialog' });
      inspectionLoadingDialog.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    inspectionLoadingDialog.resolve = resolve;
    inspectionLoadingDialog.reject = reject;
    inspectionLoadingDialog.options = options;
    inspectionLoadingDialog.state = true;

    return promise;
  };

  const closeInspectionLoadingDialog = () => {
    inspectionLoadingDialog.state = false;
  };

  const openInspectionResultDialog = (options = {}) => {
    if (!inspectionResultDialog.init) {
      const componentModule = defineAsyncComponent(() => import('@/components/dialog/InspectionResult.vue'));
      addComponents({ component: componentModule, id: 'inspectionResultDialog' });
      inspectionResultDialog.init = true;
    }

    const {
      promise,
      reject,
      resolve,
    } = Promise.withResolvers();

    inspectionResultDialog.resolve = resolve;
    inspectionResultDialog.reject = reject;
    inspectionResultDialog.options = { ...options };
    inspectionResultDialog.state = true;

    return promise;
  };

  const closeInspectionResultDialog = () => {
    inspectionResultDialog.state = false;
  };

  return {
    components,
    alertDialog,
    openAlertDialog,
    closeAlertDialog,
    changePassword,
    openChangePasswordDialog,
    closeChangePasswordDialog,
    departmentForm,
    openDepartmentForm,
    closeDepartmentForm,
    administrator,
    openAdministrator,
    closeAdministrator,
    rejectPhoneData,
    openRejectPhoneData,
    closeRejectPhoneData,
    scheduleSearchResultsDownload,
    openScheduleSearchResultsDownload,
    closeScheduleSearchResultsDownload,
    scheduledFileDownload,
    openScheduledFileDownload,
    closeScheduledFileDownload,
    imageManagement,
    openImageManagement,
    closeImageManagement,

    imageView,
    openImageView,
    closeImageView,

    imageList,
    openImageList,
    closeImageList,

    mfaAuth,
    openMfaAuth,
    closeMfaAuth,

    temporaryPasswordChange,
    openTemporaryPasswordChange,
    closeTemporaryPasswordChange,

    alimTalkConfirmDialog,
    openAlimTalkConfirmDialog,
    closeAlimTalkConfirmDialog,

    alimTalkInquiryDialog,
    openAlimTalkInquiryDialog,
    closeAlimTalkInquiryDialog,

    applicationExample,
    openApplicationExampleDialog,
    closeApplicationExampleDialog,

    confirmDialog,
    openConfirmDialog,
    closeConfirmDialog,

    inspectionLoadingDialog,
    openInspectionLoadingDialog,
    closeInspectionLoadingDialog,

    inspectionResultDialog,
    openInspectionResultDialog,
    closeInspectionResultDialog,
  };
});

export default componentsStore;
